import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAddressData } from "../features/address/addressSlice";
import { getUserId } from "../features/feature1/feature1Slice";
import {
  fetchUserwallet,
  setWallet,
} from "../features/userwallet/userwalletSlice";
import { fetchMediaData } from "../features/orders/ordersSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { ImCross } from "react-icons/im";
import { FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import {
  setSelectedAddress,
  setOrderType,
  setCouponCode,
  setCheckoutData,
  setUploadedPrescription,
  clearUploadedPrescription,
  placeOrder,
} from "../features/checkout/checkoutSlice";
import { clearCart } from "../features/cart/cartSlice"; // Import the clearCart action
import Cookies from "js-cookie"; // Add this import if you're still using cookies

const CheckOut = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loyalties, setLoyalties] = useState([]);

  const clearCartData = () => {
    // Clear Redux store
    dispatch(clearCart());
    // Clear cookies if you're still using them
    Cookies.remove("cart");
    console.log("Cart cleared successfully"); // Debug log
  };

  // Redux state selectors
  const userId = useSelector((state) => state.feature1.userId);
  const { addressData, status } = useSelector((state) => state.address);
  const { userwallet, wallet } = useSelector((state) => state.userwallet);
  const coinDiscount = wallet && loyalties ? loyalties.coins_amount : 0;
  const { mediaData } = useSelector((state) => state.orders);
  const {
    selectedAddress,
    orderType,
    couponCode,
    checkoutData,
    uploadedPrescription,
    loading,
    error,
  } = useSelector((state) => state.checkout);

  useEffect(() => {
    if (!userId) {
      dispatch(getUserId());
    } else {
      dispatch(fetchAddressData());
      dispatch(fetchUserwallet());
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (location.state && location.state.checkoutData) {
      dispatch(setCheckoutData(location.state.checkoutData));
      dispatch(
        fetchMediaData([{ products: location.state.checkoutData.products }])
      );
    }
  }, [location.state, dispatch]);

  const handleAddressSelection = (addressId) => {
    dispatch(setSelectedAddress(addressId));
  };

  const handleOrderTypeChange = (event) => {
    dispatch(setOrderType(event.target.value));
  };

  const handlePrescriptionUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      dispatch(
        setUploadedPrescription({
          file: file,
          name: file.name,
          date: new Date().toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
        })
      );
    }
  };

  const handleRemovePrescription = () => {
    dispatch(clearUploadedPrescription());
  };

  const getUnitPrice = (product) => {
    return product.price / product.quantity;
  };

  const calculateTotalPrice = (products) => {
    return products.reduce((total, product) => {
      const unitPrice = getUnitPrice(product);
      return total + unitPrice * product.quantity;
    }, 0);
  };

  const handleCheckout = async () => {
    if (!selectedAddress || !checkoutData) {
      toast.error(
        "Please select an address and ensure cart data is available."
      );
      return;
    }

    if (orderType === "PREPAID") {
      handleRazorpayPayment();
    } else {
      const formData = new FormData();

      formData.append("address_id", selectedAddress);
      formData.append("shipping_charge", checkoutData.shipping_charge);
      formData.append("order_type", orderType);
      formData.append("coupon_code", couponCode);

      if (userwallet && userwallet._id && wallet) {
        formData.append("user_wallet_id", userwallet._id);
      } else {
        formData.append("user_wallet_id", null);
      }

      checkoutData.products.forEach((product, index) => {
        const unitPrice = getUnitPrice(product);
        formData.append(`products[${index}][product_id]`, product.product_id);
        formData.append(`products[${index}][quantity]`, product.quantity);
        formData.append(`products[${index}][price]`, unitPrice.toString());
        formData.append(`products[${index}][discount_id]`, product.discount_id);
        formData.append(`products[${index}][media_id]`, product.media_id);
        formData.append(
          `products[${index}][product_variant_id]`,
          product.product_variant_id
        );
      });

      if (uploadedPrescription) {
        formData.append("files", uploadedPrescription.file);
      }

      const result = await dispatch(placeOrder(formData));

      if (placeOrder.fulfilled.match(result)) {
        clearCartData();

        toast.success("Order placed successfully!");
        // Navigate to home page after successful payment
        navigate("/", {
          state: { orderDetails: result.payload },
        });
      } else {
        toast.error("Failed to place order. Please try again.");
      }
    }
  };

  const handleRazorpayPayment = async () => {
    try {
      const token = localStorage.getItem("token");

      // Create order with your API's expected format
      const formData = new FormData();
      formData.append("address_id", selectedAddress);
      formData.append("shipping_charge", checkoutData.shipping_charge);
      formData.append("order_type", "PREPAID");
      formData.append("coupon_code", couponCode || "");

      if (userwallet && userwallet._id && wallet) {
        formData.append("user_wallet_id", userwallet._id);
      } else {
        formData.append("user_wallet_id", null);
      }

      checkoutData.products.forEach((product, index) => {
        const unitPrice = getUnitPrice(product);
        formData.append(`products[${index}][product_id]`, product.product_id);
        formData.append(`products[${index}][quantity]`, product.quantity);
        formData.append(`products[${index}][price]`, unitPrice.toString());
        formData.append(`products[${index}][discount_id]`, product.discount_id);
        formData.append(`products[${index}][media_id]`, product.media_id);
        formData.append(
          `products[${index}][product_variant_id]`,
          product.product_variant_id
        );
      });

      if (uploadedPrescription) {
        formData.append("files", uploadedPrescription.file);
      }

      // Create order
      const orderResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/orders`,
        formData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const { id, amount, currency } = orderResponse.data;

      if (!window.Razorpay) {
        toast.error("Razorpay SDK not loaded properly");
        return;
      }

      const amountInPaise = Math.round(amount * 100);

      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const userName = userData.name || "Guest User";
      const userEmail = userData.email || "guest@example.com";

      const options = {
        key: process.env.REACT_APP_RAZORPAY_API_KEY,
        amount: amountInPaise,
        currency: currency,
        name: "Janhit Chemist",
        description: "Order Payment",
        order_id: id,

        handler: async function (response) {
          const { razorpay_order_id, razorpay_payment_id, razorpay_signature } =
            response;

          try {
            // Verify payment on your server
            const verificationResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/payment/verify`,
              { razorpay_order_id, razorpay_payment_id, razorpay_signature },
              {
                headers: {
                  Authorization: token,
                },
              }
            );

            // Check for the error flag in the response
            if (!verificationResponse.data.error) {
              // Clear cart after successful payment
              clearCartData(); // Ensure cart is cleared
              toast.success("Payment successful!");
              navigate("/", {
                state: { orderDetails: verificationResponse.data.order },
              });
            } else {
              toast.error("Payment verification failed.");
            }
          } catch (error) {
            toast.error("Payment verification failed");
            console.error("Payment verification error:", error);
          }
        },
        prefill: {
          name: userName,
          email: userEmail,
        },
        theme: {
          color: "#61dafb",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error("Failed to process payment. Please try again.");
      console.error("Payment error:", error);
    }
  };

  useEffect(() => {
    const fetchLoyalties = async () => {
      try {
        // Get token from localStorage
        const token = localStorage.getItem("token"); // Get token from localStorage

        // Make the API call
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/loyalities`,
          {
            headers: {
              Authorization: token,
            },
          }
        );

        // Set response data to state
        setLoyalties(response.data);
      } catch (error) {
        console.error("Error fetching loyalties:", error);
      }
    };

    fetchLoyalties();
  }, []);

  useEffect(() => {
    localStorage.getItem("wallet") === "true"
      ? dispatch(setWallet(true))
      : dispatch(setWallet(false));
  }, []);
  return (
    <div>
      <title>Checkout</title>
      <div className="checkout-page">
        <h2>Checkout</h2>
        <div className="checkout-page-container">
          <div className="checkout-container-1">
            <div className="delivery-details">
              <div className="address-selection">
                <h4 className="selection-title">SELECT DELIVERY ADDRESS</h4>
                {status === "loading" && (
                  <p className="status-message">Loading addresses...</p>
                )}
                {status === "failed" && (
                  <p className="status-message error">
                    Error loading addresses.
                  </p>
                )}
                {addressData && addressData.length > 0 ? (
                  <div className="address-grid">
                    {addressData.map((address) => (
                      <div
                        key={address._id}
                        className={`address-box ${
                          selectedAddress === address._id ? "selected" : ""
                        }`}
                        onClick={() => handleAddressSelection(address._id)}
                      >
                        <div className="address-header">
                          <span className="address-type">
                            {address.address_type}
                          </span>
                          {selectedAddress === address._id && (
                            <span className="check-mark">✓</span>
                          )}
                        </div>
                        <p className="bill-to">{address.bill_to}</p>
                        <p className="address-line">{address.address}</p>
                        <p className="address-line">
                          {address.city}, {address.state} - {address.pincode}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-address-message">
                    No addresses found. Please add a delivery address.
                  </p>
                )}
              </div>

              <div className="payment-content">
                <h4>PAYMENT</h4>
                <p>All transactions are secured and encrypted.</p>
                <div className="payment-type-selection">
                  <h4>Payment Type</h4>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="orderType"
                      id="cod"
                      value="COD"
                      checked={orderType === "COD"}
                      onChange={handleOrderTypeChange}
                    />
                    <label className="form-check-label" htmlFor="cod">
                      Cash on Delivery (COD)
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="orderType"
                      id="prepaid"
                      value="PREPAID"
                      checked={orderType === "PREPAID"}
                      onChange={handleOrderTypeChange}
                    />
                    <label className="form-check-label" htmlFor="prepaid">
                      Online Payment (Razorpay)
                    </label>
                  </div>
                </div>
                
              </div>

              {checkoutData &&
                checkoutData.products.some(
                  (product) => product.isRequirePrescription
                ) && (
                  <div className="prescription-upload mt-4">
                    <div className="card">
                      <div className="card-body">
                        <h4 className="card-title mb-3">
                          <FaUpload className="me-3" />
                          Upload Prescription Now
                        </h4>
                        <p className="text-muted mb-3">Add Photos:</p>
                        <div className="row g-3 mb-3">
                          <div className="col-12">
                            <button
                              className="btn btn-outline-secondary w-100"
                              onClick={() =>
                                document
                                  .getElementById("prescription-upload")
                                  .click()
                              }
                            >
                              <i className="bi bi-images me-2"></i>
                              Gallery
                            </button>
                          </div>
                        </div>
                        <input
                          type="file"
                          id="prescription-upload"
                          className="d-none"
                          onChange={handlePrescriptionUpload}
                          accept="image/*,application/pdf"
                        />
                        {uploadedPrescription && (
                          <div className="d-flex align-items-center justify-content-between bg-light p-2 rounded">
                            <div className="d-flex align-items-center">
                              <div
                                className="bg-secondary rounded me-3"
                                style={{ width: "40px", height: "40px" }}
                              ></div>
                              <div>
                                <p className="mb-0 fw-medium">
                                  {uploadedPrescription.name}
                                </p>
                                <small className="text-muted">
                                  {uploadedPrescription.date}
                                </small>
                              </div>
                            </div>
                            <button
                              onClick={handleRemovePrescription}
                              className="btn btn-sm btn-light"
                            >
                              <ImCross />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              <button
                className="pay-now-btn"
                onClick={handleCheckout}
                disabled={loading}
              >
                {loading ? "Processing..." : "PLACE ORDER"}
              </button>
            </div>
          </div>

          {checkoutData && (
            <div className="checkout-container-2">
              <div className="checkout-product-details">
                {checkoutData.products.map((product, index) => (
                  <div key={index} className="ckt-title border-bottom">
                    <div className="imagess">
                      {mediaData[product.media_id] ? (
                        <img
                          src={mediaData[product.media_id].url}
                          alt={product.title}
                        />
                      ) : (
                        <p>Loading image...</p>
                      )}
                      <label>(Quantity: {product.quantity})</label>
                    </div>
                    <div className="headings">
                      <h5>{product.title}</h5>
                      <span>
                        <del>MRP ₹{getUnitPrice(product)?.toFixed(2)}</del> ₹
                        {getUnitPrice(product)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="checkout-product-price">
                <div className="price-lines">
                  <span>Total</span>
                  <span>
                    ₹{calculateTotalPrice(checkoutData?.products).toFixed(2)}
                  </span>
                </div>
                <div className="price-lines">
                  <span>Shipping Charge</span>
                  <span>₹{checkoutData?.shipping_charge.toFixed(2)}</span>
                </div>
                {userwallet && userwallet?._id && wallet && (
                  <div className="price-lines">
                    <span>Coin Discount</span>
                    <span>-₹{coinDiscount?.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="total-amount-pay">
                <div className="total-pay">
                  <span>Total Amount to Pay</span>
                  <span>
                    ₹
                    {(
                      Number(calculateTotalPrice(checkoutData.products)) +
                      Number(checkoutData?.shipping_charge) -
                      Number(coinDiscount?.toFixed(2))
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckOut;
